const defaultBrandName = "Doteb";
const defaultPublicDomain = "doteb.com";

function readEnv(...names: string[]): string | undefined {
	for (const name of names) {
		const value = process.env[name]?.trim();
		if (value) {
			return value;
		}
	}
	return undefined;
}

function trimTrailingSlash(value: string): string {
	return value.replace(/\/+$/, "");
}

function normalizePath(path = ""): string {
	if (!path) {
		return "";
	}
	return path.startsWith("/") ? path : `/${path}`;
}

function normalizeDomain(value: string): string {
	try {
		const url = value.includes("://")
			? new URL(value)
			: new URL(`https://${value}`);
		return url.hostname;
	} catch {
		return value.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
	}
}

function buildUrl(domain: string, path = ""): string {
	return `https://${domain}${normalizePath(path)}`;
}

function publicSubdomain(subdomain: string): string {
	return `${subdomain}.${getPublicDomain()}`;
}

export function getBrandName(): string {
	return (
		readEnv("PUBLIC_BRAND_NAME", "NEXT_PUBLIC_BRAND_NAME") ?? defaultBrandName
	);
}

export function getPublicDomain(): string {
	return normalizeDomain(
		readEnv(
			"PUBLIC_DOMAIN",
			"NEXT_PUBLIC_DOMAIN",
			"NEXT_PUBLIC_PUBLIC_DOMAIN",
		) ?? defaultPublicDomain,
	);
}

export function getMarketingUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv("PUBLIC_URL", "APP_URL", "UI_URL", "NEXT_PUBLIC_APP_URL") ??
				buildUrl(getPublicDomain()),
		) + normalizePath(path)
	);
}

export function getApiUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv("PUBLIC_API_URL", "API_URL", "NEXT_PUBLIC_API_URL") ??
				buildUrl(publicSubdomain("api")),
		) + normalizePath(path)
	);
}

export function getConsoleApiUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv(
				"CONSOLE_API_URL",
				"PUBLIC_CONSOLE_API_URL",
				"API_URL",
				"NEXT_PUBLIC_CONSOLE_API_URL",
			) ?? buildUrl(publicSubdomain("console-api")),
		) + normalizePath(path)
	);
}

export function getDocsUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv("PUBLIC_DOCS_URL", "DOCS_URL", "NEXT_PUBLIC_DOCS_URL") ??
				buildUrl(publicSubdomain("docs")),
		) + normalizePath(path)
	);
}

export function getChatUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv(
				"PUBLIC_CHAT_URL",
				"CHAT_URL",
				"PLAYGROUND_URL",
				"NEXT_PUBLIC_CHAT_URL",
			) ?? buildUrl(publicSubdomain("chat")),
		) + normalizePath(path)
	);
}

export function getCodeUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv("PUBLIC_CODE_URL", "CODE_URL", "NEXT_PUBLIC_CODE_URL") ??
				buildUrl(publicSubdomain("code")),
		) + normalizePath(path)
	);
}

export function getDevpassUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv("PUBLIC_DEVPASS_URL", "DEVPASS_URL", "NEXT_PUBLIC_DEVPASS_URL") ??
				buildUrl(publicSubdomain("devpass")),
		) + normalizePath(path)
	);
}

export function getAdminUrl(path = ""): string {
	return (
		trimTrailingSlash(
			readEnv("PUBLIC_ADMIN_URL", "ADMIN_URL", "NEXT_PUBLIC_ADMIN_URL") ??
				buildUrl(publicSubdomain("admin")),
		) + normalizePath(path)
	);
}

export function getContactEmail(): string {
	return (
		readEnv("CONTACT_EMAIL", "NEXT_PUBLIC_CONTACT_EMAIL") ??
		`contact@${getPublicDomain()}`
	);
}

export function getMailFromEmail(): string {
	return (
		readEnv("RESEND_FROM_EMAIL") ??
		`${getBrandName()} <contact@mail.${getPublicDomain()}>`
	);
}

export function getMailReplyToEmail(): string {
	return readEnv("RESEND_REPLY_TO_EMAIL") ?? getContactEmail();
}

export function getTelemetryBeaconUrl(): string {
	return (
		readEnv("TELEMETRY_BEACON_URL") ??
		buildUrl(publicSubdomain("internal"), "/beacon")
	);
}
